import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';

type NotificationStatus = 'all' | 'unread' | 'read';
type DeliveryChannel = 'app' | 'email';

interface NotificationControl {
  key: string;
  label: string;
  group: string;
  description: string;
  recipients: string;
  trigger: string;
  icon: string;
  iconClass: string;
  hasEmail: boolean;
  appKey: string;
  emailKey: string;
  appDefault: boolean;
  emailDefault: boolean;
}

@Component({
  selector: 'app-admin-notification-center',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="h-full min-h-0 overflow-hidden bg-[#F4F7FA] p-4 md:p-5">
      <div class="max-w-[1540px] mx-auto h-full min-h-0 flex flex-col">
        <div class="flex flex-col xl:flex-row xl:items-end justify-between gap-3 mb-4 shrink-0">
          <div class="min-w-0">
            <p class="text-[9px] font-bold uppercase tracking-[0.16em] text-[#1A6FA6] mb-1">Command Center</p>
            <h1 class="text-xl font-bold text-slate-950 tracking-tight">Notification Center</h1>
            <p class="text-slate-500 text-[11px]">Live inbox for workflow alerts, unread work, and operational exceptions.</p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <a routerLink="/admin/notification-controls" class="h-9 px-3 rounded-md bg-white border border-slate-200 text-slate-700 text-[11px] font-bold hover:bg-slate-50 flex items-center gap-2">
              <i class="ti ti-adjustments-horizontal text-[14px]"></i>
              Notification controls
            </a>
            <button type="button" (click)="reloadAll()" [disabled]="loading()" class="h-9 px-3 rounded-md bg-white border border-slate-200 text-slate-700 text-[11px] font-bold hover:bg-slate-50 disabled:opacity-60 flex items-center gap-2">
              <i class="ti" [class.ti-refresh]="!loading()" [class.ti-loader]="loading()" [class.animate-spin]="loading()"></i>
              Refresh
            </button>
          </div>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 shrink-0">
          <div class="metric"><p>Inbox total</p><strong>{{ items().length }}</strong></div>
          <div class="metric"><p>Unread</p><strong class="text-[#1A6FA6]">{{ unreadCount() }}</strong></div>
          <div class="metric"><p>High priority</p><strong class="text-rose-600">{{ highPriorityCount() }}</strong></div>
          <div class="metric"><p>Categories</p><strong>{{ categories.length }}</strong></div>
        </div>

        <div class="grid gap-4 flex-1 min-h-0">
          <section class="bg-white rounded-lg border border-[#E7EEF5] overflow-hidden flex flex-col min-h-0">
            <div class="px-4 py-3 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
              <div>
                <p class="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Live Inbox</p>
                <p class="text-xs text-slate-500 mt-0.5">Audit what the current user has received and act on unread work.</p>
              </div>
              <button type="button" (click)="markAllRead()" [disabled]="unreadCount() === 0" class="h-8 px-3 rounded-md bg-[#0C3A5A] text-white text-[11px] font-bold hover:bg-[#082d46] disabled:opacity-45 flex items-center gap-2">
                <i class="ti ti-checks text-[14px]"></i>
                Mark all read
              </button>
            </div>

            <div class="grid xl:grid-cols-[220px_minmax(0,1fr)] flex-1 min-h-0">
              <aside class="border-b xl:border-b-0 xl:border-r border-slate-100 bg-slate-50/45 xl:overflow-y-auto">
                <button type="button" (click)="setCategory('all')" class="category-row" [ngClass]="selectedCategory() === 'all' ? 'active' : ''">
                  <span class="icon bg-slate-100 text-slate-600"><i class="ti ti-inbox"></i></span>
                  <span class="min-w-0 flex-1 text-left"><strong>All</strong><small>Complete inbox</small></span>
                  <b>{{ items().length }}</b>
                </button>
                @for (category of categories; track category.key) {
                  <button type="button" (click)="setCategory(category.key)" class="category-row" [ngClass]="selectedCategory() === category.key ? 'active' : ''">
                    <span class="icon" [ngClass]="category.class"><i class="ti" [class]="'ti-' + category.icon"></i></span>
                    <span class="min-w-0 flex-1 text-left"><strong>{{ category.label }}</strong><small>{{ category.caption }}</small></span>
                    <b>{{ categoryCount(category.key) }}</b>
                  </button>
                }
              </aside>

              <div class="min-w-0 min-h-0 flex flex-col">
                <div class="px-4 py-3 border-b border-slate-100 grid md:grid-cols-[minmax(0,1fr)_140px_140px] gap-2">
                  <div class="relative min-w-[220px]">
                    <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[15px]"></i>
                    <input type="text" [ngModel]="search()" (ngModelChange)="search.set($event)" placeholder="Search notifications, routes, references..."
                           class="w-full h-9 pl-9 pr-3 rounded-md border border-slate-200 bg-slate-50 text-[12px] text-slate-700 outline-none focus:bg-white focus:border-[#1A6FA6] placeholder:text-slate-400">
                  </div>
                  <select [ngModel]="status()" (ngModelChange)="status.set($event)" class="h-9 rounded-md border border-slate-200 bg-slate-50 px-3 text-[12px] text-slate-700 outline-none focus:border-[#1A6FA6]">
                    <option value="all">All statuses</option>
                    <option value="unread">Unread only</option>
                    <option value="read">Read only</option>
                  </select>
                  <select [ngModel]="priority()" (ngModelChange)="priority.set($event)" class="h-9 rounded-md border border-slate-200 bg-slate-50 px-3 text-[12px] text-slate-700 outline-none focus:border-[#1A6FA6]">
                    <option value="">All priorities</option>
                    <option value="high">High priority</option>
                    <option value="normal">Normal priority</option>
                    <option value="low">Low priority</option>
                  </select>
                </div>

                @if (loading()) {
                  <div class="p-10 text-center text-slate-500 text-sm flex-1">
                    <i class="ti ti-loader animate-spin text-xl"></i>
                    <p class="mt-2">Loading notifications...</p>
                  </div>
                } @else {
                  <div class="divide-y divide-slate-100 flex-1 min-h-0 overflow-y-auto">
                    @for (item of filteredItems(); track item.id) {
                      <article class="px-4 py-3 flex flex-col md:flex-row md:items-start gap-3 hover:bg-slate-50/80 transition-colors" [ngClass]="!item.read_at ? 'bg-[#F6FBFF]' : ''">
                        <div class="w-10 h-10 rounded-md flex items-center justify-center shrink-0" [ngClass]="itemIconClass(item)">
                          <i class="ti text-[18px]" [class]="'ti-' + itemIcon(item)"></i>
                        </div>
                        <div class="min-w-0 flex-1">
                          <div class="flex flex-wrap items-center gap-2 mb-1">
                            <span class="text-[10px] font-bold uppercase tracking-[0.12em]" [ngClass]="categoryTextClass(item.category)">{{ categoryLabel(item.category) }}</span>
                            <span class="badge" [ngClass]="priorityClass(item.priority)">{{ item.priority || 'normal' }}</span>
                            @if (!item.read_at) {
                              <span class="badge bg-[#E8F4FB] text-[#1A6FA6]">Unread</span>
                            }
                          </div>
                          <h2 class="text-[13px] font-bold text-slate-950 leading-snug">{{ item.title }}</h2>
                          <p class="text-[11px] text-slate-600 mt-1 leading-relaxed">{{ item.body || 'No extra message supplied.' }}</p>
                          <div class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-400">
                            <span><i class="ti ti-clock text-[12px]"></i> {{ relativeTime(item.created_at) }}</span>
                            @if (item.reference_type || item.reference_id) {
                              <span><i class="ti ti-tag text-[12px]"></i> {{ item.reference_type || 'Reference' }} {{ item.reference_id || '' }}</span>
                            }
                            @if (item.route) {
                              <span class="truncate max-w-[360px]"><i class="ti ti-route text-[12px]"></i> {{ item.route }}</span>
                            }
                          </div>
                        </div>
                        <div class="flex md:flex-col gap-2 md:items-end shrink-0">
                          <a [routerLink]="notificationRoute(item)" (click)="openNotification(item)" class="h-8 px-3 rounded-md bg-[#0C3A5A] text-white text-[11px] font-bold hover:bg-[#082d46] flex items-center gap-2">
                            <i class="ti ti-external-link text-[13px]"></i>
                            Open
                          </a>
                          @if (!item.read_at) {
                            <button type="button" (click)="markRead(item)" class="h-8 px-3 rounded-md border border-slate-200 bg-white text-slate-600 text-[11px] font-bold hover:bg-slate-50 flex items-center gap-2">
                              <i class="ti ti-check text-[13px]"></i>
                              Mark read
                            </button>
                          }
                        </div>
                      </article>
                    } @empty {
                      <div class="p-10 text-center">
                        <div class="w-12 h-12 mx-auto rounded-lg bg-slate-100 flex items-center justify-center text-slate-400">
                          <i class="ti ti-bell-off text-xl"></i>
                        </div>
                        <p class="text-sm font-bold text-slate-900 mt-3">No notifications found</p>
                        <p class="text-xs text-slate-500 mt-1">Try another business part, status, priority, or search term.</p>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .metric { background:#fff; border:1px solid #E7EEF5; border-radius:8px; padding:12px 14px; min-width:0; }
    .metric p { font-size:10px; font-weight:800; text-transform:uppercase; letter-spacing:.12em; color:#94a3b8; margin:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .metric strong { display:block; margin-top:4px; font-size:20px; line-height:1.1; color:#0f172a; }
    .field-label { display:block; margin-bottom:.375rem; font-size:.75rem; font-weight:700; color:#475569; }
    .field-input { width:100%; height:2.5rem; border-radius:.5rem; border:1px solid #e2e8f0; background:#f8fafc; padding:0 .75rem; font-size:.875rem; color:#1e293b; outline:none; }
    .field-input:focus { border-color:#1A6FA6; background:#fff; }
    .mini-btn { height:28px; padding:0 10px; border-radius:6px; background:#f8fafc; border:1px solid #e2e8f0; color:#475569; font-size:10px; font-weight:800; }
    .mini-btn:hover { background:#fff; border-color:#cbd5e1; color:#0f172a; }
    .toggle-card { display:flex; align-items:flex-start; gap:10px; border:1px solid #e2e8f0; border-radius:8px; background:#f8fafc; padding:12px; cursor:pointer; transition:.18s ease; }
    .toggle-card.compact { padding:10px 12px; }
    .toggle-card:hover, .toggle-card.is-on { background:#fff; border-color:#cbd5e1; }
    .toggle-card input { position:absolute; opacity:0; pointer-events:none; }
    .toggle-card strong { display:block; font-size:12px; line-height:1.2; color:#0f172a; }
    .toggle-card small { display:block; margin-top:3px; font-size:10px; line-height:1.35; color:#64748b; }
    .toggle-icon { width:34px; height:34px; border-radius:8px; display:flex; align-items:center; justify-content:center; flex:none; font-size:17px; }
    .switch { width:36px; height:20px; border-radius:999px; background:#cbd5e1; position:relative; flex:none; margin-top:2px; transition:.18s ease; }
    .switch span { position:absolute; width:16px; height:16px; top:2px; left:2px; border-radius:999px; background:#fff; box-shadow:0 1px 2px rgba(15,23,42,.18); transition:.18s ease; }
    .toggle-card input:checked ~ .switch { background:#1A6FA6; }
    .toggle-card input:checked ~ .switch span { transform:translateX(16px); }
    .switch-cell { display:flex; align-items:center; justify-content:center; min-height:68px; cursor:pointer; }
    .switch-cell input { position:absolute; opacity:0; pointer-events:none; }
    .switch-cell span { width:36px; height:20px; border-radius:999px; background:#cbd5e1; position:relative; transition:.18s ease; }
    .switch-cell span::after { content:''; position:absolute; width:16px; height:16px; top:2px; left:2px; border-radius:999px; background:white; box-shadow:0 1px 2px rgba(15,23,42,.18); transition:.18s ease; }
    .switch-cell input:checked + span { background:#1A6FA6; }
    .switch-cell input:checked + span::after { transform:translateX(16px); }
    .switch-cell input:disabled + span { background:#e2e8f0; }
    .category-row { width:100%; display:flex; align-items:center; gap:9px; padding:11px 12px; border-bottom:1px solid #e2e8f0; color:#475569; }
    .category-row:hover, .category-row.active { background:#F5FBFE; }
    .category-row.active { box-shadow:inset 3px 0 0 #1A6FA6; }
    .category-row .icon { width:31px; height:31px; border-radius:8px; display:flex; align-items:center; justify-content:center; flex:none; }
    .category-row strong { display:block; font-size:11px; color:#0f172a; line-height:1.1; }
    .category-row small { display:block; font-size:9px; color:#64748b; margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .category-row b { font-size:10px; color:#64748b; background:#fff; border-radius:999px; min-width:24px; height:20px; display:flex; align-items:center; justify-content:center; padding:0 7px; }
    .badge { display:inline-flex; height:20px; align-items:center; border-radius:999px; padding:0 8px; font-size:10px; font-weight:800; text-transform:capitalize; }
  `]
})
export class AdminNotificationCenterComponent implements OnInit {
  private api = inject(ApiService);

  loading = signal(false);
  settingsLoading = signal(false);
  saving = signal(false);
  items = signal<any[]>([]);
  selectedCategory = signal('all');
  status = signal<NotificationStatus>('all');
  priority = signal('');
  search = signal('');
  initialSettings = signal<any | null>(null);
  settings = signal<any>(this.defaultSettings());

  notificationControls: NotificationControl[] = [
    { key: 'job_assignment', label: 'Job assignments', group: 'Dispatch', description: 'Worker alerts when a job is allocated or changed.', recipients: 'Assigned workers', trigger: 'Job allocation', icon: 'briefcase', iconClass: 'bg-sky-50 text-[#1A6FA6]', hasEmail: true, appKey: 'notify_job_assignment_app', emailKey: 'notify_job_assignment_email', appDefault: true, emailDefault: true },
    { key: 'project_assignment', label: 'Project work assignments', group: 'Projects', description: 'Worker alerts for project allocations and scheduled project work.', recipients: 'Assigned project workers', trigger: 'Project allocation', icon: 'building-skyscraper', iconClass: 'bg-violet-50 text-violet-600', hasEmail: true, appKey: 'notify_project_assignment_app', emailKey: 'notify_project_assignment_email', appDefault: true, emailDefault: true },
    { key: 'leave_requests', label: 'Leave requests', group: 'Workforce', description: 'Admin review alerts and employee decision updates.', recipients: 'Approvers and employee', trigger: 'Submit or decision', icon: 'calendar-time', iconClass: 'bg-amber-50 text-amber-600', hasEmail: true, appKey: 'notify_leave_requests_app', emailKey: 'notify_leave_requests_email', appDefault: true, emailDefault: true },
    { key: 'fleet_updates', label: 'Fleet updates', group: 'Fleet', description: 'Vehicle assignment and fleet responsibility alerts.', recipients: 'Assigned worker', trigger: 'Vehicle assignment', icon: 'truck', iconClass: 'bg-teal-50 text-teal-600', hasEmail: false, appKey: 'notify_fleet_updates_app', emailKey: 'notify_fleet_updates_email', appDefault: true, emailDefault: false },
    { key: 'project_phase_reviews', label: 'Project phase reviews', group: 'Projects', description: 'Needs-changes and office review alerts for project forms.', recipients: 'Workers and office', trigger: 'Phase review', icon: 'clipboard-check', iconClass: 'bg-indigo-50 text-indigo-600', hasEmail: true, appKey: 'notify_project_phase_reviews_app', emailKey: 'notify_project_phase_reviews_email', appDefault: true, emailDefault: true },
    { key: 'quote_responses', label: 'Quote responses', group: 'Financials', description: 'Admin alerts when customers accept or decline quotes.', recipients: 'Admin team', trigger: 'Customer response', icon: 'file-dollar', iconClass: 'bg-emerald-50 text-emerald-600', hasEmail: false, appKey: 'notify_quote_responses_app', emailKey: 'notify_quote_responses_email', appDefault: true, emailDefault: false },
    { key: 'customer_feedback', label: 'Customer feedback', group: 'Customers', description: 'Feedback request emails and submitted feedback alerts.', recipients: 'Customers and managers', trigger: 'Ready or completed', icon: 'message-star', iconClass: 'bg-rose-50 text-rose-600', hasEmail: true, appKey: 'notify_customer_feedback_app', emailKey: 'notify_customer_feedback_email', appDefault: true, emailDefault: true },
    { key: 'document_updates', label: 'Documents and AI', group: 'Documents', description: 'Document, declaration, insurance and as-built workflow alerts.', recipients: 'Office users', trigger: 'Document workflow', icon: 'folders', iconClass: 'bg-slate-100 text-slate-600', hasEmail: false, appKey: 'notify_document_updates_app', emailKey: 'notify_document_updates_email', appDefault: true, emailDefault: false },
    { key: 'safety_compliance', label: 'Safety and compliance', group: 'Compliance', description: 'Audit, visa and safety action alerts.', recipients: 'Office and managers', trigger: 'Safety or audit issue', icon: 'shield-check', iconClass: 'bg-orange-50 text-orange-600', hasEmail: false, appKey: 'notify_safety_compliance_app', emailKey: 'notify_safety_compliance_email', appDefault: true, emailDefault: false },
    { key: 'operations_alerts', label: 'Operations alerts', group: 'Operations', description: 'Long-waiting unassigned jobs and idle active-shift alerts.', recipients: 'Dispatch and workers', trigger: 'Operational exception', icon: 'alarm', iconClass: 'bg-red-50 text-red-600', hasEmail: false, appKey: 'notify_operations_alerts_app', emailKey: 'notify_operations_alerts_email', appDefault: true, emailDefault: false },
  ];

  categories = [
    { key: 'dispatch', label: 'Dispatch', caption: 'Jobs and scheduling', icon: 'list-details', class: 'bg-sky-50 text-[#1A6FA6]' },
    { key: 'workforce', label: 'Workforce', caption: 'Staff and leave', icon: 'briefcase-2', class: 'bg-indigo-50 text-indigo-600' },
    { key: 'fleet', label: 'Fleet', caption: 'Vehicle alerts', icon: 'truck', class: 'bg-teal-50 text-teal-600' },
    { key: 'financials', label: 'Financials', caption: 'Quotes and POs', icon: 'file-dollar', class: 'bg-emerald-50 text-emerald-600' },
    { key: 'projects_documents', label: 'Projects & Docs', caption: 'Project workflows', icon: 'folders', class: 'bg-violet-50 text-violet-600' },
    { key: 'customer_experience', label: 'Customers', caption: 'Feedback', icon: 'message-star', class: 'bg-amber-50 text-amber-600' },
    { key: 'compliance', label: 'Compliance', caption: 'Safety and audits', icon: 'shield-check', class: 'bg-rose-50 text-rose-600' },
    { key: 'system', label: 'System', caption: 'Platform alerts', icon: 'bell', class: 'bg-slate-100 text-slate-600' }
  ];

  unreadCount = computed(() => this.items().filter(item => !item.read_at).length);
  highPriorityCount = computed(() => this.items().filter(item => item.priority === 'high').length);
  activeAppControls = computed(() => this.notificationControls.filter(item => !!this.settings()[item.appKey]).length);
  activeEmailControls = computed(() => this.notificationControls.filter(item => item.hasEmail && !!this.settings()[item.emailKey]).length);
  emailCapableControls = computed(() => this.notificationControls.filter(item => item.hasEmail).length);
  activeFeedbackTriggers = computed(() => Number(!!this.settings().feedback_request_on_ready_for_office) + Number(!!this.settings().feedback_request_on_completed));
  settingsDirty = computed(() => {
    const initial = this.initialSettings();
    return !!initial && JSON.stringify(this.normalizedSettings(this.settings())) !== JSON.stringify(this.normalizedSettings(initial));
  });

  selectedCategoryCount = computed(() => this.filteredByCategory().length);
  filteredItems = computed(() => {
    const searchTerm = this.search().trim().toLowerCase();
    return this.filteredByCategory().filter(item => {
      if (this.status() === 'unread' && item.read_at) return false;
      if (this.status() === 'read' && !item.read_at) return false;
      if (this.priority() && item.priority !== this.priority()) return false;
      if (!searchTerm) return true;
      const haystack = [item.title, item.body, item.type, item.reference_type, item.reference_id, item.route, item.category].join(' ').toLowerCase();
      return haystack.includes(searchTerm);
    });
  });

  ngOnInit() {
    this.reloadAll();
  }

  reloadAll() {
    this.loadNotifications();
  }

  loadSettings() {
    this.settingsLoading.set(true);
    this.api.getCompanySettings().subscribe({
      next: (res: any) => {
        const next = this.settingsFromResponse(res?.success ? res.data || {} : {});
        this.settings.set(next);
        this.initialSettings.set({ ...next });
        this.settingsLoading.set(false);
      },
      error: () => {
        this.settingsLoading.set(false);
        showToast('error', 'Failed to load notification controls.');
      }
    });
  }

  loadNotifications() {
    this.loading.set(true);
    this.api.getNotifications({ limit: 500 }).subscribe({
      next: (res: any) => {
        const data = res.success ? (res.data || {}) : {};
        this.items.set((data.items || []).map((item: any) => ({ ...item, category: item.category || this.inferCategory(item) })));
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        showToast('error', 'Failed to load notifications.');
      }
    });
  }

  updateSetting(key: string, value: any) {
    this.settings.update(settings => ({ ...settings, [key]: value }));
  }

  setAllChannel(channel: DeliveryChannel, enabled: boolean) {
    const masterKey = channel === 'app' ? 'in_app_notifications_enabled' : 'email_notifications_enabled';
    this.settings.update(settings => {
      const next = { ...settings, [masterKey]: enabled };
      this.notificationControls.forEach(item => {
        if (channel === 'app') next[item.appKey] = enabled;
        if (channel === 'email' && item.hasEmail) next[item.emailKey] = enabled;
      });
      return next;
    });
  }

  saveSettings() {
    const ccEmail = String(this.settings().outgoing_cc_email || '').trim();
    if (ccEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ccEmail)) {
      showToast('error', 'Enter a valid global CC email address.');
      return;
    }

    const cleaned = { ...this.settings(), outgoing_cc_email: ccEmail };
    this.settings.set(cleaned);
    this.saving.set(true);
    this.api.updateCompanySettings(this.normalizedSettings(cleaned)).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res.success) {
          this.initialSettings.set({ ...cleaned });
          showToast('success', 'Notification controls saved.');
        } else {
          showToast('error', res.message || 'Failed to save notification controls.');
        }
      },
      error: () => {
        this.saving.set(false);
        showToast('error', 'Network error while saving notification controls.');
      }
    });
  }

  setCategory(category: string) {
    this.selectedCategory.set(category);
  }

  markRead(item: any) {
    this.api.markNotificationRead(Number(item.id)).subscribe({
      next: () => this.items.update(items => items.map(current => current.id === item.id ? { ...current, read_at: current.read_at || new Date().toISOString() } : current)),
      error: () => showToast('error', 'Failed to update notification.')
    });
  }

  markAllRead() {
    this.api.markAllNotificationsRead().subscribe({
      next: () => {
        const now = new Date().toISOString();
        this.items.update(items => items.map(item => ({ ...item, read_at: item.read_at || now })));
      },
      error: () => showToast('error', 'Failed to update notifications.')
    });
  }

  openNotification(item: any) {
    if (!item.read_at) this.markRead(item);
  }

  notificationRoute(item: any): string {
    return item.route || '/admin/dashboard';
  }

  channelTitle(item: NotificationControl, channel: DeliveryChannel): string {
    if (channel === 'email' && !item.hasEmail) return 'Email is not used by this workflow yet.';
    const masterOff = channel === 'app' ? !this.settings().in_app_notifications_enabled : !this.settings().email_notifications_enabled;
    if (masterOff) return `${channel === 'app' ? 'In-app' : 'Email'} delivery is disabled globally.`;
    return `${item.label} ${channel === 'app' ? 'in-app' : 'email'} delivery.`;
  }

  categoryCount(category: string): number {
    return this.items().filter(item => item.category === category).length;
  }

  categoryLabel(category: string): string {
    return this.categories.find(item => item.key === category)?.label || 'System';
  }

  categoryTextClass(category: string): string {
    const classes: Record<string, string> = {
      dispatch: 'text-[#1A6FA6]',
      workforce: 'text-indigo-600',
      fleet: 'text-teal-600',
      financials: 'text-emerald-600',
      projects_documents: 'text-violet-600',
      customer_experience: 'text-amber-600',
      compliance: 'text-rose-600',
      system: 'text-slate-500'
    };
    return classes[category] || classes['system'];
  }

  itemIcon(item: any): string {
    const category = item.category || this.inferCategory(item);
    if (category === 'dispatch') return 'list-details';
    if (category === 'workforce') return 'briefcase-2';
    if (category === 'fleet') return 'truck';
    if (category === 'financials') return 'file-dollar';
    if (category === 'projects_documents') return 'folders';
    if (category === 'customer_experience') return 'message-star';
    if (category === 'compliance') return 'shield-check';
    return 'bell';
  }

  itemIconClass(item: any): string {
    if (item.priority === 'high') return 'bg-rose-50 text-rose-600';
    const category = this.categories.find(current => current.key === item.category);
    return category?.class || 'bg-slate-100 text-slate-600';
  }

  priorityClass(priority: string): string {
    if (priority === 'high') return 'bg-rose-50 text-rose-600';
    if (priority === 'low') return 'bg-slate-100 text-slate-500';
    return 'bg-emerald-50 text-emerald-600';
  }

  relativeTime(value: string): string {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return 'No date';
    const minutes = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return date.toLocaleDateString('en-NZ', { timeZone: 'Pacific/Auckland' });
  }

  private filteredByCategory(): any[] {
    const category = this.selectedCategory();
    if (category === 'all') return this.items();
    return this.items().filter(item => item.category === category);
  }

  private inferCategory(item: any): string {
    const text = [item.type, item.reference_type, item.route, item.title].join(' ').toLowerCase();
    if (/fleet|vehicle|truck|van/.test(text)) return 'fleet';
    if (/quote|invoice|purchase|work.order|financial|payment|po/.test(text)) return 'financials';
    if (/project|phase|declaration|as.built|insurance|document|ai/.test(text)) return 'projects_documents';
    if (/feedback|customer/.test(text)) return 'customer_experience';
    if (/visa|audit|safety|compliance/.test(text)) return 'compliance';
    if (/job|dispatch|schedule|allocated|ready/.test(text)) return 'dispatch';
    if (/leave|timesheet|shift|staff|worker|employee/.test(text)) return 'workforce';
    return 'system';
  }

  private settingsFromResponse(s: any): any {
    const next = this.defaultSettings();
    next.email_notifications_enabled = this.toBool(s.email_notifications_enabled, true);
    next.in_app_notifications_enabled = this.toBool(s.in_app_notifications_enabled, true);
    next.feedback_request_on_ready_for_office = this.toBool(s.feedback_request_on_ready_for_office, true);
    next.feedback_request_on_completed = this.toBool(s.feedback_request_on_completed, false);
    next.outgoing_cc_email = s.outgoing_cc_email || '';
    this.notificationControls.forEach(item => {
      next[item.appKey] = this.toBool(s[item.appKey], item.appDefault);
      next[item.emailKey] = this.toBool(s[item.emailKey], item.emailDefault);
    });
    return next;
  }

  private defaultSettings(): any {
    return {
      email_notifications_enabled: true,
      in_app_notifications_enabled: true,
      feedback_request_on_ready_for_office: true,
      feedback_request_on_completed: false,
      outgoing_cc_email: '',
      notify_job_assignment_app: true,
      notify_job_assignment_email: true,
      notify_project_assignment_app: true,
      notify_project_assignment_email: true,
      notify_leave_requests_app: true,
      notify_leave_requests_email: true,
      notify_fleet_updates_app: true,
      notify_fleet_updates_email: false,
      notify_project_phase_reviews_app: true,
      notify_project_phase_reviews_email: true,
      notify_quote_responses_app: true,
      notify_quote_responses_email: false,
      notify_customer_feedback_app: true,
      notify_customer_feedback_email: true,
      notify_document_updates_app: true,
      notify_document_updates_email: false,
      notify_safety_compliance_app: true,
      notify_safety_compliance_email: false,
      notify_operations_alerts_app: true,
      notify_operations_alerts_email: false
    };
  }

  private normalizedSettings(settings: any): any {
    const payload: any = {
      email_notifications_enabled: settings.email_notifications_enabled ? '1' : '0',
      in_app_notifications_enabled: settings.in_app_notifications_enabled ? '1' : '0',
      feedback_request_on_ready_for_office: settings.feedback_request_on_ready_for_office ? '1' : '0',
      feedback_request_on_completed: settings.feedback_request_on_completed ? '1' : '0',
      outgoing_cc_email: String(settings.outgoing_cc_email || '').trim()
    };
    this.notificationControls.forEach(item => {
      payload[item.appKey] = settings[item.appKey] ? '1' : '0';
      payload[item.emailKey] = settings[item.emailKey] ? '1' : '0';
    });
    return payload;
  }

  private toBool(value: any, fallback: boolean): boolean {
    if (value === undefined || value === null || value === '') return fallback;
    const normalized = String(value).trim().toLowerCase();
    if (['1', 'true', 'yes', 'on', 'enabled'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'off', 'disabled'].includes(normalized)) return false;
    return fallback;
  }
}
